<template>
  <div class="catalog-insert-page policy-detail-page">
    <header class="policy-detail-page__header catalog-insert-page__header">
      <div class="catalog-insert-page__header-main">
        <div class="catalog-insert-page__header-text">
          <h1 class="policy-detail-page__title">{{ pageTitle }}</h1>
          <p v-if="pageSubtitle" class="catalog-insert-page__subtitle">{{ pageSubtitle }}</p>
        </div>
      </div>
    </header>

    <div
      v-loading="loading"
      class="catalog-insert-page__body"
      element-loading-custom-class="newsrc-catalog-loading"
      :element-loading-background="'var(--agent-loading-overlay-bg)'"
    >
      <template v-if="product">
        <section class="policy-detail-section catalog-insert-page__product-section">
          <article class="hezabat-card policy-detail-contract">
            <div class="policy-detail-contract__grid">
              <div class="policy-detail-contract__col">
                <h3 class="policy-detail-block-title">{{ $t("general_info") }}</h3>
                <table class="policy-detail-table-info">
                  <tbody>
                    <tr v-for="row in productInfoRows" :key="row.key">
                      <td class="policy-detail-table-info__label">{{ row.label }}</td>
                      <td class="policy-detail-table-info__value">{{ row.value || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="policy-detail-contract__col">
                <h3 class="policy-detail-block-title">{{ $t("catalogIncludedServices") }}</h3>
                <ul v-if="serviceList.length" class="catalog-insert-info-list">
                  <li v-for="(name, idx) in serviceList" :key="`svc-${idx}`">{{ name }}</li>
                </ul>
                <p v-else class="catalog-insert-info-empty">—</p>
                <h3 class="policy-detail-block-title policy-detail-block-title--spaced">
                  {{ $t("ineligibility") }}
                </h3>
                <ul v-if="exceptList.length" class="catalog-insert-info-list catalog-insert-info-list--exc">
                  <li v-for="(name, idx) in exceptList" :key="`exc-${idx}`">{{ name }}</li>
                </ul>
                <p v-else class="catalog-insert-info-empty">—</p>
              </div>
            </div>
          </article>
        </section>

        <section class="policy-detail-section">
          <div class="catalog-insert-section-head">
            <h2 class="policy-detail-section__title">{{ $t("catalogMainInsured") }}</h2>
            <button
              v-if="!mainEntry"
              type="button"
              class="insured-dialog-btn insured-dialog-btn--primary"
              @click="openAddMain"
            >
              {{ $t("catalogInsuredQueueAdd") }}
            </button>
          </div>

          <article class="hezabat-card catalog-insert-person-card">
          <div v-if="mainRow" class="catalog-person-queue__table-wrap">
            <table class="catalog-person-queue__table">
              <thead>
                <tr>
                  <th>{{ $t("pinCode") }}</th>
                  <th>{{ $t("fullNameLabel") }}</th>
                  <th>{{ $t("sex") }}</th>
                  <th>{{ $t("birthDateAge") }}</th>
                  <th>{{ $t("mobilePhone") }}</th>
                  <th class="catalog-person-queue__actions-col" aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                <tr class="catalog-person-queue__row--main">
                  <td>{{ mainRow.pin || "—" }}</td>
                  <td>{{ mainRow.fullName }}</td>
                  <td>{{ mainRow.sexLabel }}</td>
                  <td>{{ mainRow.birthDateLabel }}</td>
                  <td>{{ mainRow.phone }}</td>
                  <td class="catalog-person-queue__actions-col">
                    <div class="catalog-person-queue__actions">
                      <button
                        type="button"
                        class="catalog-person-queue__edit"
                        :title="$t('catalogInsuredQueueEdit')"
                        :aria-label="$t('catalogInsuredQueueEdit')"
                        @click="openEditMain"
                      >
                        <svg class="catalog-person-queue__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="catalog-person-queue__remove"
                        :title="$t('catalogInsuredQueueRemove')"
                        @click="removeMain"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="catalog-person-queue__empty">{{ $t("catalogInsertMainEmpty") }}</p>
          </article>
        </section>

        <section class="policy-detail-section policy-detail-section--last">
          <div class="catalog-insert-section-head">
            <h2 class="policy-detail-section__title">
              {{ $t("catalogInsuredQueueTitle") }}
              <span v-if="cardRows.length" class="catalog-person-queue__count">{{ cardRows.length }}</span>
            </h2>
            <div class="catalog-insert-section-head__actions">
              <button
                v-if="canAddMainAsCard"
                type="button"
                class="insured-dialog-btn insured-dialog-btn--success-outline"
                @click="promptAddMainToCards"
              >
                {{ $t("catalogInsertAddMainAsCard") }}
              </button>
              <button
                type="button"
                class="insured-dialog-btn insured-dialog-btn--primary"
                @click="openAddCard"
              >
                {{ $t("catalogInsuredQueueAdd") }}
              </button>
            </div>
          </div>

          <article class="hezabat-card catalog-insert-person-card">
          <div v-if="cardRows.length" class="catalog-person-queue__table-wrap">
            <table class="catalog-person-queue__table">
              <thead>
                <tr>
                  <th>{{ $t("pinCode") }}</th>
                  <th>{{ $t("fullNameLabel") }}</th>
                  <th>{{ $t("sex") }}</th>
                  <th>{{ $t("birthDateAge") }}</th>
                  <th>{{ $t("mobilePhone") }}</th>
                  <th class="catalog-person-queue__actions-col" aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in cardRows" :key="row.id">
                  <td>{{ row.pin || "—" }}</td>
                  <td>{{ row.fullName }}</td>
                  <td>{{ row.sexLabel }}</td>
                  <td>{{ row.birthDateLabel }}</td>
                  <td>{{ row.phone }}</td>
                  <td class="catalog-person-queue__actions-col">
                    <div class="catalog-person-queue__actions">
                      <button
                        type="button"
                        class="catalog-person-queue__edit"
                        :title="$t('catalogInsuredQueueEdit')"
                        :aria-label="$t('catalogInsuredQueueEdit')"
                        @click="openEditCard(row.id)"
                      >
                        <svg class="catalog-person-queue__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="catalog-person-queue__remove"
                        :title="$t('catalogInsuredQueueRemove')"
                        @click="removeCard(row.id)"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="catalog-person-queue__empty">{{ $t("catalogInsertCardsEmpty") }}</p>
          </article>
        </section>
      </template>

      <div v-else-if="!loading" class="catalog-insert-page__missing">
        <p>{{ $t("catalogInsertProductMissing") }}</p>
        <button type="button" class="insured-dialog-btn insured-dialog-btn--primary" @click="goBack">
          {{ $t("catalogInsertBack") }}
        </button>
      </div>
    </div>

    <footer v-if="product" class="catalog-insert-page__footer catalog-insert-page__footer--fixed">
      <div class="catalog-insert-page__footer-total">
        <span class="catalog-insert-page__footer-total-label">
          {{ $t("catalogInsertContractTotal") }}
        </span>
        <strong class="catalog-insert-page__footer-total-value">{{ contractTotalLabel }}</strong>
        <span v-if="contractTotalHint" class="catalog-insert-page__footer-total-hint">
          {{ contractTotalHint }}
        </span>
      </div>
      <div class="catalog-insert-page__footer-actions">
        <button type="button" class="insured-dialog-btn insured-dialog-btn--muted" :disabled="submitting" @click="goBack">
          {{ $t("btnCancel") }}
        </button>
        <button
          type="button"
          class="insured-dialog-btn catalog-insert-page__submit-btn"
          :disabled="submitting || !mainEntry"
          @click="submitInsert"
        >
          <i v-if="submitting" class="fa fa-spinner fa-spin" aria-hidden="true" />
          {{ submitting ? $t("catalogInsertCreating") : $t("catalogInsertCreateContract") }}
        </button>
      </div>
    </footer>

    <CatalogInsuredCardDialog
      v-model="personDialogVisible"
      :draft="personDialogDraft"
      :dialog-title="personDialogTitle"
      @confirm="onPersonConfirm"
    />
  </div>
</template>

<script>
import Utils from "@/project/Source/utils.js";
import MedicalAgent from "../Source/medicalAgent";
import CatalogInsuredCardDialog from "./CatalogInsuredCardDialog.vue";
import {
  buildAgentRequestInsertPayload,
  buildProdDetailRouteAfterInsert,
  parseAgentRequestInsertResult,
  normalizePin,
} from "./catalogAgentRequest.js";
import {
  createQueuedPerson,
  updateQueuedPerson,
  removeQueuedPerson,
  findQueuedPersonPinConflict,
  findQueuedPersonIndex,
  formatQueuedPersonName,
  formatQueuedPersonBirthDate,
} from "./catalogInsuredQueue.js";
import {
  formatProductMoneyInteger,
  productPremiumNumber,
  productIsStatic,
  resolveSumInsuredCalculated,
  productPeriodLabel,
  productServiceList,
  productExceptList,
  productAdminChargeLabel,
  productTextField,
} from "./catalogProductDisplay.js";
import { productPartyKind } from "./catalogProductParty.js";
import { readStashedCatalogProduct } from "./catalogSelectedProduct.js";

const DIALOG_TARGET = { MAIN: "main", CARD: "card" };

export default {
  name: "CatalogProductInsertPage",
  components: { CatalogInsuredCardDialog },
  data() {
    return {
      loading: false,
      submitting: false,
      product: null,
      mainEntry: null,
      cards: [],
      personDialogVisible: false,
      personDialogTarget: DIALOG_TARGET.MAIN,
      personDialogDraft: null,
      editingCardId: null,
    };
  },
  computed: {
    productId() {
      return this.$route.params.productId;
    },
    pageTitle() {
      return this.product?.prodName || this.$t("catalogInsertTitle");
    },
    pageSubtitle() {
      const parts = [];
      const kind = productPartyKind(this.product || {});
      if (kind === "legal") parts.push(this.$t("partyLegalEntity"));
      else if (kind === "individual") parts.push(this.$t("partyNaturalPerson"));
      if (this.product?.productId != null) parts.push(`#${this.product.productId}`);
      return parts.join(" · ");
    },
    partyLine() {
      const kind = productPartyKind(this.product || {});
      if (kind === "legal") return this.$t("partyLegalEntity");
      if (kind === "individual") return this.$t("partyNaturalPerson");
      return "";
    },
    productInfoRows() {
      const rows = [
        {
          key: "productId",
          label: this.$t("transactionProductLabel"),
          value: this.product?.productId != null ? String(this.product.productId) : null,
        },
        { key: "premium", label: this.$t("premiumLabel"), value: this.premiumLabel },
        { key: "sumInsured", label: this.$t("sumInsuredLabel"), value: this.sumInsuredLabel },
        { key: "period", label: this.$t("labelPeriod"), value: this.periodLabel },
      ];
      if (this.adminChargeLabel) {
        rows.push({
          key: "adminCharge",
          label: this.$t("adminCharge"),
          value: this.adminChargeLabel,
        });
      }
      const eligibility = productTextField(
        this.product,
        "eligibility",
        "Eligibility",
        "targetGroup"
      );
      const payment = productTextField(
        this.product,
        "paymentCondition",
        "PaymentCondition",
        "paymentTerms"
      );
      if (eligibility) {
        rows.push({ key: "eligibility", label: this.$t("eligibility"), value: eligibility });
      }
      if (payment) {
        rows.push({ key: "payment", label: this.$t("paymentCondition"), value: payment });
      }
      return rows;
    },
    premiumLabel() {
      const n = productPremiumNumber(this.product);
      const s = n != null ? formatProductMoneyInteger(n) : null;
      return s ? `${s} ${this.$t("currencyAZN")}` : "—";
    },
    sumInsuredLabel() {
      const count = this.mainEntry ? Math.max(this.contractPersonCount, 1) : 0;
      const n = count > 0 ? resolveSumInsuredCalculated(this.product, count) : null;
      const s = n != null ? formatProductMoneyInteger(n) : null;
      return s ? `${s} ${this.$t("currencyAZN")}` : "—";
    },
    periodLabel() {
      return productPeriodLabel(this.product) || "—";
    },
    adminChargeLabel() {
      return productAdminChargeLabel(this.product);
    },
    serviceList() {
      return productServiceList(this.product);
    },
    exceptList() {
      return productExceptList(this.product);
    },
    personDialogTitle() {
      return this.personDialogTarget === DIALOG_TARGET.MAIN
        ? this.$t("catalogMainInsured")
        : this.$t("catalogDialogTitleInsuredCard");
    },
    mainRow() {
      if (!this.mainEntry) return null;
      return this.buildDisplayRow("__main__", this.mainEntry.values);
    },
    cardRows() {
      return this.cards.map((entry) => this.buildDisplayRow(entry.id, entry.values));
    },
    canAddMainAsCard() {
      const mainPin = normalizePin(this.mainEntry?.values?.pin);
      if (!mainPin) return false;
      return findQueuedPersonIndex(this.cards, mainPin) < 0;
    },
    contractPersonCount() {
      return this.cards.length;
    },
    contractUnitPremium() {
      return productPremiumNumber(this.product);
    },
    /** IsStatic products: premium is fixed, not multiplied by insured count. */
    isStaticPremium() {
      return productIsStatic(this.product);
    },
    contractTotalAmount() {
      const premium = this.contractUnitPremium;
      if (premium == null || this.contractPersonCount === 0) return null;
      if (this.isStaticPremium) return premium;
      return premium * this.contractPersonCount;
    },
    contractTotalLabel() {
      if (this.contractTotalAmount == null) return "—";
      return `${formatProductMoneyInteger(this.contractTotalAmount)} ${this.$t("currencyAZN")}`;
    },
    contractTotalHint() {
      const premium = this.contractUnitPremium;
      if (premium == null || this.contractPersonCount === 0) return "";
      if (this.isStaticPremium) return "";
      return this.$t("catalogInsertContractTotalHint", {
        premium: formatProductMoneyInteger(premium),
        count: this.contractPersonCount,
      });
    },
  },
  mounted() {
    this.loadProduct();
  },
  methods: {
    buildDisplayRow(id, values) {
      return {
        id,
        pin: normalizePin(values?.pin),
        fullName: formatQueuedPersonName(values),
        sexLabel:
          values?.sex === "f"
            ? this.$t("female") || "Qadın"
            : values?.sex === "m"
              ? this.$t("male") || "Kişi"
              : "—",
        birthDateLabel: formatQueuedPersonBirthDate(values?.dateOfBirth),
        phone: values?.mobilePhone || "—",
      };
    },
    loadProduct() {
      const stashed = readStashedCatalogProduct(this.productId);
      if (stashed) {
        this.product = stashed;
        return;
      }

      this.loading = true;
      MedicalAgent.GetProducts({ requestCorrelationId: crypto.randomUUID() })
        .then((response) => {
          const rows = response?.data?.data || [];
          this.product =
            rows.find((p) => String(p.productId) === String(this.productId)) || null;
        })
        .catch((err) => {
          this.product = null;
          if (err?.response?.status === 401) {
            this.$router.push("/signIn/catalog");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goBack() {
      this.$router.push("/catalog");
    },
    openAddMain() {
      this.personDialogTarget = DIALOG_TARGET.MAIN;
      this.personDialogDraft = null;
      this.editingCardId = null;
      this.personDialogVisible = true;
    },
    openEditMain() {
      if (!this.mainEntry) return;
      this.personDialogTarget = DIALOG_TARGET.MAIN;
      this.personDialogDraft = { ...this.mainEntry };
      this.editingCardId = null;
      this.personDialogVisible = true;
    },
    openAddCard() {
      this.personDialogTarget = DIALOG_TARGET.CARD;
      this.personDialogDraft = null;
      this.editingCardId = null;
      this.personDialogVisible = true;
    },
    openEditCard(id) {
      const entry = this.cards.find((card) => card.id === id);
      if (!entry) return;
      this.personDialogTarget = DIALOG_TARGET.CARD;
      this.personDialogDraft = {
        values: { ...entry.values },
        searchedPin: entry.searchedPin,
        isAsan: entry.isAsan === true,
      };
      this.editingCardId = id;
      this.personDialogVisible = true;
    },
    onPersonConfirm(values, pin, isAsan) {
      if (this.personDialogTarget === DIALOG_TARGET.MAIN) {
        this.mainEntry = {
          values: { ...values, pin },
          searchedPin: pin,
          isAsan: isAsan === true,
        };
        this.personDialogVisible = false;
        this.$nextTick(() => this.promptAddMainToCards());
        return;
      }

      if (this.editingCardId) {
        const conflict = findQueuedPersonPinConflict(this.cards, pin, this.editingCardId);
        if (conflict) {
          Utils.Alert(this.$t("catalogInsuredQueuePinConflict"));
          return;
        }
        this.cards = updateQueuedPerson(this.cards, this.editingCardId, values, pin, isAsan);
      } else {
        const conflict = findQueuedPersonPinConflict(this.cards, pin);
        if (conflict) {
          Utils.Alert(this.$t("catalogInsuredQueuePinConflict"));
          return;
        }
        this.cards = [...this.cards, createQueuedPerson(values, pin, isAsan)];
      }

      this.personDialogVisible = false;
      this.editingCardId = null;
    },
    promptAddMainToCards() {
      if (!this.canAddMainAsCard) return;
      Utils.DeleteDialog(
        this.$t("catalogInsertAddMainAsCardConfirm"),
        this.$t("catalogInsertAddMainAsCardConfirmTitle")
      ).then((result) => {
        if (result.isConfirmed) this.addMainAsCard();
      });
    },
    addMainAsCard() {
      if (!this.canAddMainAsCard || !this.mainEntry) return;
      this.cards = [
        createQueuedPerson(
          { ...this.mainEntry.values },
          this.mainEntry.searchedPin,
          this.mainEntry.isAsan === true
        ),
        ...this.cards,
      ];
    },
    removeMain() {
      this.mainEntry = null;
    },
    removeCard(id) {
      this.cards = removeQueuedPerson(this.cards, id);
    },
    async submitInsert() {
      if (this.submitting) return;
      if (!this.mainEntry?.values) {
        Utils.Alert(this.$t("catalogMainInsuredRequired"));
        return;
      }

      this.submitting = true;
      try {
        const payload = buildAgentRequestInsertPayload(
          this.productId,
          this.cards,
          this.mainEntry.values,
          {
            product: this.product,
            mainSearchedPin:
              this.mainEntry.searchedPin || normalizePin(this.mainEntry.values?.pin),
            mainIsAsan: this.mainEntry.isAsan === true,
          }
        );
        const response = await MedicalAgent.AgentRequestInsert(payload);
        const inserted = parseAgentRequestInsertResult(response);
        if (!inserted?.reqId) {
          Utils.Alert(this.$t("error"));
          return;
        }
        const route = buildProdDetailRouteAfterInsert(this.productId, inserted.reqId);
        if (route) {
          this.$router.push(route).catch(() => {});
        } else {
          this.$router.push("/policies");
        }
      } catch (error) {
        const validationKey =
          error?.message && String(error.message).startsWith("catalog")
            ? error.message
            : "";
        const msg =
          (validationKey && this.$t(validationKey)) ||
          Utils.formatApiErrors(error?.response?.data?.errors) ||
          Utils.FormatErrorWithCorrelationId(error);
        if (msg) Utils.Alert(msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style src="@/assets/scss/hezabat.scss" lang="scss"></style>
<style src="@/assets/scss/policy-detail.scss" lang="scss"></style>
